import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { FileText, Plus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"

import { PresentationBrandLogoCard } from "./presentation-brand-logo-card"
import {
  DEFAULT_FRAME_FILENAME,
  extraLogoSlots,
  FRAME_SLOT_ID,
  generationModeWhenDisablingMarkdown,
  markdownLineCount,
  pickFrameSlot,
  slugFromFilename,
  specHasMarkdown,
} from "./presentation-brand-spec-utils"
import { PresentationBrandStatusCard } from "./presentation-brand-status-card"
import {
  useDeletePresentationBrandAssetMutation,
  usePresentationBrandQuery,
  usePresentationTemplateCatalogQuery,
  usePutPresentationBrandMutation,
  useUploadPresentationBrandAssetMutation,
} from "../hooks/admin-queries"

export function PresentationBrandSpecPanel() {
  const { t } = useTranslation()
  const { data, isLoading } = usePresentationBrandQuery()
  const catalogQuery = usePresentationTemplateCatalogQuery()
  const putMutation = usePutPresentationBrandMutation()
  const uploadMutation = useUploadPresentationBrandAssetMutation()
  const deleteMutation = useDeletePresentationBrandAssetMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorDraft, setEditorDraft] = useState("")
  const [dragOver, setDragOver] = useState(false)

  const editorDirty = Boolean(data && editorOpen && editorDraft !== data.spec_markdown)
  useRegisterAdminDirty("presentation-brand-spec-editor", editorDirty)

  const saveSpec = async (args: {
    enabled: boolean
    specMarkdown: string
    specFilename: string | null
    generationMode?: "markdown" | "managed" | "ask"
    reason: string
  }) => {
    if (!data?.hash) {
      toast.error(
        t("adminDashboard.systemConfig.presentationBrand.missingHash", {
          defaultValue: "Missing hash — reload the page and try again",
        })
      )
      return
    }
    try {
      await putMutation.mutateAsync({
        enabled: args.enabled,
        spec_filename: args.specFilename,
        spec_markdown: args.specMarkdown,
        generation_mode: args.generationMode,
        expectedHash: data.hash,
        reason: args.reason,
      })
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.presentationBrand.saveFailed", {
          defaultValue: "Failed to save presentation brand spec",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  const handleMarkdownFile = async (file: File | undefined) => {
    if (!file || !data) {
      return
    }
    const text = await file.text()
    try {
      await saveSpec({
        enabled: data.enabled,
        specMarkdown: text,
        specFilename: file.name,
        reason: "Uploaded presentation brand spec markdown",
      })
      toast.success(
        t("adminDashboard.systemConfig.presentationBrand.specUploaded", {
          defaultValue: "Spec file saved",
        })
      )
    } catch {
      /* toast already shown */
    }
  }

  const handleSaveEditor = async () => {
    if (!data) {
      return
    }
    try {
      await saveSpec({
        enabled: data.enabled,
        specMarkdown: editorDraft,
        specFilename: data.spec_filename,
        reason: "Edited presentation brand spec markdown",
      })
      setEditorOpen(false)
      toast.success(
        t("adminDashboard.systemConfig.presentationBrand.saved", {
          defaultValue: "Presentation brand spec saved",
        })
      )
    } catch {
      /* toast already shown */
    }
  }

  const handleSetEnabled = async (enabled: boolean) => {
    if (!data) {
      return
    }
    const generationMode = enabled
      ? "markdown"
      : generationModeWhenDisablingMarkdown(catalogQuery.isSuccess)
    const disableReason =
      generationMode === "managed"
        ? "Disabled markdown brand spec; company template restored"
        : "Disabled markdown brand spec; ask the user because no company template is configured"
    try {
      await saveSpec({
        enabled,
        specMarkdown: data.spec_markdown,
        specFilename: data.spec_filename,
        generationMode,
        reason: enabled ? "Enabled markdown brand spec for new decks" : disableReason,
      })
      if (enabled) {
        toast.success(
          t("adminDashboard.systemConfig.presentationBrand.nowLive", {
            defaultValue: "Markdown spec is live for new decks",
          })
        )
      } else if (generationMode === "managed") {
        toast.success(
          t("adminDashboard.systemConfig.presentationBrand.nowTemplate", {
            defaultValue: "New decks will use the company template again",
          })
        )
      } else {
        toast.success(
          t("adminDashboard.systemConfig.presentationBrand.statusAskDetail", {
            defaultValue:
              "Explorer asks whether to use the company template before building a deck.",
          })
        )
      }
    } catch {
      /* toast already shown */
    }
  }

  const handleUploadSlot = async (args: {
    slotId: string
    file: File
    filename: string
    required: boolean
    role: string
  }) => {
    try {
      await uploadMutation.mutateAsync({
        slotId: args.slotId,
        file: args.file,
        meta: { filename: args.filename, required: args.required, role: args.role },
      })
      toast.success(
        t("adminDashboard.systemConfig.presentationBrand.assetSaved", {
          defaultValue: "Logo saved",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.presentationBrand.assetFailed", {
          defaultValue: "Failed to save logo",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
    }
  }

  const handleDeleteSlot = async (slotId: string) => {
    try {
      await deleteMutation.mutateAsync(slotId)
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.presentationBrand.assetDeleteFailed", {
          defaultValue: "Failed to remove logo",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
    }
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const frame = pickFrameSlot(data.assets)
  const extras = extraLogoSlots(data.assets)
  const lineCount = markdownLineCount(data.spec_markdown)

  return (
    <div className="space-y-6">
      <PresentationBrandStatusCard
        data={data}
        pending={putMutation.isPending}
        onGoLive={() => void handleSetEnabled(true)}
        onUseTemplate={() => void handleSetEnabled(false)}
      />

      <section className="space-y-3">
        <div>
          <h5 className="text-sm font-semibold">
            {t("adminDashboard.systemConfig.presentationBrand.specTitle", {
              defaultValue: "Brand spec",
            })}
          </h5>
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.systemConfig.presentationBrand.specHelp", {
              defaultValue:
                "The markdown file is the generation contract. Upload it, then edit if you need to tweak.",
            })}
          </p>
        </div>

        {specHasMarkdown(data.spec_markdown) ? (
          <div className="border-border flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
                <FileText className="text-muted-foreground size-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {data.spec_filename ||
                    t("adminDashboard.systemConfig.presentationBrand.untitledSpec", {
                      defaultValue: "brand-spec.md",
                    })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.systemConfig.presentationBrand.specMeta", {
                    defaultValue: "{{lines}} lines",
                    lines: lineCount,
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => {
                  setEditorDraft(data.spec_markdown)
                  setEditorOpen(true)
                }}
              >
                {t("adminDashboard.systemConfig.presentationBrand.editSpec", {
                  defaultValue: "Edit",
                })}
              </Button>
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("adminDashboard.systemConfig.presentationBrand.replaceSpec", {
                  defaultValue: "Replace file",
                })}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={`border-border bg-muted/20 flex min-h-36 w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-6 text-center ${
              dragOver ? "border-primary bg-accent/40" : ""
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragLeave={() => setDragOver(false)}
            onDragOver={(event) => {
              event.preventDefault()
              setDragOver(true)
            }}
            onDrop={(event) => {
              event.preventDefault()
              setDragOver(false)
              void handleMarkdownFile(event.dataTransfer.files[0])
            }}
          >
            <div className="bg-muted flex size-12 items-center justify-center rounded-lg">
              <FileText className="text-muted-foreground size-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t("adminDashboard.systemConfig.presentationBrand.dropSpecTitle", {
                  defaultValue: "Drop the brand spec markdown",
                })}
              </p>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.systemConfig.presentationBrand.dropSpecHelp", {
                  defaultValue: "Or click to choose a .md file. Saving is immediate.",
                })}
              </p>
            </div>
          </button>
        )}

        <input
          ref={fileInputRef}
          accept=".md,text/markdown,text/plain"
          className="sr-only"
          type="file"
          onChange={(event) => {
            void handleMarkdownFile(event.target.files?.[0])
            event.target.value = ""
          }}
        />
      </section>

      <section className="space-y-3">
        <div>
          <h5 className="text-sm font-semibold">
            {t("adminDashboard.systemConfig.presentationBrand.logosTitle", {
              defaultValue: "Logos",
            })}
          </h5>
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.systemConfig.presentationBrand.logosHelp", {
              defaultValue:
                "Frame logo is required. Use a print-quality PNG from the brand guide (often 1150×600). Extra logos are optional.",
            })}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <PresentationBrandLogoCard
            required
            slot={frame?.slot ?? null}
            slotId={frame?.id ?? FRAME_SLOT_ID}
            uploading={uploadMutation.isPending}
            description={t("adminDashboard.systemConfig.presentationBrand.frameLogoHelp", {
              defaultValue: "Corner / slide-frame mark inserted as a picture, never drawn.",
            })}
            title={t("adminDashboard.systemConfig.presentationBrand.frameLogo", {
              defaultValue: "Frame logo",
            })}
            onRemove={frame ? () => void handleDeleteSlot(frame.id) : undefined}
            onUpload={(file) =>
              void handleUploadSlot({
                slotId: frame?.id ?? FRAME_SLOT_ID,
                file,
                filename: file.name || DEFAULT_FRAME_FILENAME,
                required: true,
                role: "frame_logo",
              })
            }
          />
          {extras.map((item) => (
            <PresentationBrandLogoCard
              key={item.id}
              description={item.slot.filename}
              slot={item.slot}
              slotId={item.id}
              uploading={uploadMutation.isPending}
              title={t("adminDashboard.systemConfig.presentationBrand.extraLogo", {
                defaultValue: "Extra logo",
              })}
              onRemove={() => void handleDeleteSlot(item.id)}
              onUpload={(file) =>
                void handleUploadSlot({
                  slotId: item.id,
                  file,
                  filename: file.name || item.slot.filename,
                  required: item.slot.required,
                  role: item.slot.role,
                })
              }
            />
          ))}
        </div>
        <Button
          disabled={uploadMutation.isPending}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => {
            const input = document.createElement("input")
            input.type = "file"
            input.accept = "image/png,image/jpeg,image/webp"
            input.onchange = () => {
              const file = input.files?.[0]
              if (!file) {
                return
              }
              void handleUploadSlot({
                slotId: slugFromFilename(file.name),
                file,
                filename: file.name,
                required: false,
                role: "content_logo",
              })
            }
            input.click()
          }}
        >
          <Plus className="size-4" />
          {t("adminDashboard.systemConfig.presentationBrand.addExtra", {
            defaultValue: "Add extra logo",
          })}
        </Button>
      </section>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.systemConfig.presentationBrand.editSpecTitle", {
                defaultValue: "Edit brand spec",
              })}
            </DialogTitle>
            <DialogDescription>
              {data.spec_filename ||
                t("adminDashboard.systemConfig.presentationBrand.untitledSpec", {
                  defaultValue: "brand-spec.md",
                })}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Textarea
              className="min-h-80 font-mono text-xs"
              value={editorDraft}
              onChange={(event) => setEditorDraft(event.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditorDraft(data.spec_markdown)
                setEditorOpen(false)
              }}
            >
              {t("adminDashboard.systemConfig.presentationBrand.cancel", {
                defaultValue: "Cancel",
              })}
            </Button>
            <Button
              disabled={!editorDirty || putMutation.isPending}
              type="button"
              onClick={() => void handleSaveEditor()}
            >
              {t("adminDashboard.systemConfig.presentationBrand.save", {
                defaultValue: "Save spec",
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
